/**
 * Settings Panel
 *
 * Slide-up panel for voice selection and other settings.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GEMINI_VOICES,
  GeminiVoice,
  OPENAI_REALTIME_VOICES,
  OpenAIRealtimeVoice,
} from './voices';
import { isOpenAIRealtimeRuntime, VoiceRuntime } from '../types';

export type VisualizerMode = 'full' | 'simple';

type GoogleWorkspaceAccountStatus = {
  account: string;
  email_hint?: string | null;
  user?: string | null;
  is_default?: boolean;
  connected: boolean;
  gmail_read_ready?: boolean;
  gmail_modify_ready?: boolean;
  gmail_send_ready?: boolean;
  calendar_read_ready?: boolean;
  calendar_write_ready?: boolean;
  scopes?: string[];
  error?: string;
};

type GoogleWorkspaceStatus = {
  default_account?: string | null;
  oauth_reconnect_available?: boolean;
  google_client_id?: string | null;
  accounts?: GoogleWorkspaceAccountStatus[];
};

type GoogleWorkspaceReconnectResponse = {
  success: boolean;
  account: string;
  email?: string | null;
};

type AidaCapability = {
  name: string;
  description: string;
  access: 'read' | 'write';
  confirmation: string;
  dependency: string;
  surfaces: string[];
  limitations: string[];
  available: boolean;
  availability_note?: string;
};

type CapabilityCatalog = {
  capability_registry_version: string;
  capability_count: number;
  capabilities: AidaCapability[];
};

function capabilityCategory(name: string): string {
  if (/reminder|notification/.test(name)) return 'Alerts & reminders';
  if (/email|gmail|calendar|intervine_support/.test(name)) return 'Email & calendar';
  if (/memory|conversation|decision|principle|personal_fact|life_list|open_loop|working_memory|briefing/.test(name)) return 'Memory & planning';
  if (/codex|agent_|artifact|coding_backlog/.test(name)) return 'Codex & Agent Hub';
  if (/search_web/.test(name)) return 'Current web';
  return 'AIDA system & tools';
}

type CodeResponse = {
  code?: string;
  error?: string;
  error_description?: string;
};

type CodeClient = {
  requestCode: () => void;
};

type CodeClientConfig = {
  client_id: string;
  scope: string;
  include_granted_scopes?: boolean;
  login_hint?: string;
  select_account?: boolean;
  ux_mode?: 'popup' | 'redirect';
  callback: (response: CodeResponse) => void;
  error_callback?: (error: { type?: string }) => void;
};

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  selectedVoice: GeminiVoice;
  onVoiceChange: (voice: GeminiVoice) => void;
  selectedVoiceRuntime: VoiceRuntime;
  availableVoiceRuntimes: VoiceRuntime[];
  onVoiceRuntimeChange: (runtime: VoiceRuntime) => void;
  selectedOpenAIRealtimeVoice: OpenAIRealtimeVoice;
  availableOpenAIRealtimeVoices: OpenAIRealtimeVoice[];
  onOpenAIRealtimeVoiceChange: (voice: OpenAIRealtimeVoice) => void;
  voiceSessionActive: boolean;
  visualizerMode: VisualizerMode;
  onVisualizerModeChange: (mode: VisualizerMode) => void;
  showDebugTranscript: boolean;
  onShowDebugTranscriptChange: (enabled: boolean) => void;

}

const GOOGLE_IDENTITY_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_WORKSPACE_SCOPE = [
  'openid',
  'email',
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/gmail.modify',
  'https://www.googleapis.com/auth/gmail.send',
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/calendar.calendarlist.readonly',
].join(' ');

let googleIdentityScriptPromise: Promise<void> | null = null;

function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve();
  }

  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_IDENTITY_SCRIPT_SRC}"]`
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Google script failed to load')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google script failed to load'));
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
}

async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as { detail?: string };
    if (typeof payload.detail === 'string' && payload.detail.trim()) {
      return payload.detail.trim();
    }
  } catch (_error) {
    // Ignore parse failures.
  }

  return response.statusText || `Request failed with status ${response.status}`;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  isOpen,
  onClose,
  selectedVoice,
  onVoiceChange,
  selectedVoiceRuntime,
  availableVoiceRuntimes,
  onVoiceRuntimeChange,
  selectedOpenAIRealtimeVoice,
  availableOpenAIRealtimeVoices,
  onOpenAIRealtimeVoiceChange,
  voiceSessionActive,
  visualizerMode,
  onVisualizerModeChange,
  showDebugTranscript,
  onShowDebugTranscriptChange,
}) => {
  const [workspaceStatus, setWorkspaceStatus] = useState<GoogleWorkspaceStatus | null>(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState('');
  const [workspaceNotice, setWorkspaceNotice] = useState('');
  const [reconnectingAccount, setReconnectingAccount] = useState<string | null>(null);
  const [capabilityCatalog, setCapabilityCatalog] = useState<CapabilityCatalog | null>(null);
  const [capabilityLoading, setCapabilityLoading] = useState(false);
  const [capabilityError, setCapabilityError] = useState('');
  const [capabilityQuery, setCapabilityQuery] = useState('');

  const refreshWorkspaceStatus = useCallback(async () => {
    setWorkspaceLoading(true);
    setWorkspaceError('');
    try {
      const response = await fetch('/api/google-workspace/status', {
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!response.ok) {
        throw new Error(await parseErrorMessage(response));
      }
      setWorkspaceStatus((await response.json()) as GoogleWorkspaceStatus);
    } catch (error) {
      setWorkspaceError(
        error instanceof Error ? error.message : 'Failed to load Google Workspace status.'
      );
    } finally {
      setWorkspaceLoading(false);
    }
  }, []);

  const refreshCapabilities = useCallback(async () => {
    setCapabilityLoading(true);
    setCapabilityError('');
    try {
      const response = await fetch('/api/capabilities', {
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(await parseErrorMessage(response));
      setCapabilityCatalog((await response.json()) as CapabilityCatalog);
    } catch (error) {
      setCapabilityError(error instanceof Error ? error.message : 'Failed to load AIDA capabilities.');
    } finally {
      setCapabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void refreshWorkspaceStatus();
    void refreshCapabilities();
  }, [isOpen, refreshCapabilities, refreshWorkspaceStatus]);

  const groupedCapabilities = useMemo(() => {
    const query = capabilityQuery.trim().toLowerCase();
    const filtered = (capabilityCatalog?.capabilities || []).filter((capability) =>
      !query || `${capability.name} ${capability.description} ${capability.limitations.join(' ')}`.toLowerCase().includes(query)
    );
    return filtered.reduce<Record<string, AidaCapability[]>>((groups, capability) => {
      const category = capabilityCategory(capability.name);
      (groups[category] ||= []).push(capability);
      return groups;
    }, {});
  }, [capabilityCatalog, capabilityQuery]);

  const handleReconnect = useCallback(
    async (account: GoogleWorkspaceAccountStatus) => {
      const clientId = workspaceStatus?.google_client_id?.trim();
      const loginHint = account.user || account.email_hint || undefined;

      if (!clientId) {
        setWorkspaceError('Google OAuth is not configured for reconnect.');
        return;
      }

      if (!workspaceStatus?.oauth_reconnect_available) {
        setWorkspaceError('Google Workspace reconnect is not available right now.');
        return;
      }

      setWorkspaceError('');
      setWorkspaceNotice('');
      setReconnectingAccount(account.account);

      try {
        await loadGoogleIdentityScript();
        const oauth2 = (window as Window & {
          google?: { accounts?: { oauth2?: { initCodeClient: (config: CodeClientConfig) => CodeClient } } };
        }).google?.accounts?.oauth2;
        if (!oauth2) {
          throw new Error('Google authorization library did not load.');
        }

        const codeClient = oauth2.initCodeClient({
          client_id: clientId,
          scope: GOOGLE_WORKSPACE_SCOPE,
          include_granted_scopes: true,
          login_hint: loginHint,
          select_account: true,
          ux_mode: 'popup',
          callback: async (response: CodeResponse) => {
            if (response.error || !response.code) {
              setWorkspaceError(
                response.error_description || response.error || 'Google authorization did not return a code.'
              );
              setReconnectingAccount(null);
              return;
            }

            try {
              const reconnectResponse = await fetch('/api/google-workspace/reconnect', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XmlHttpRequest',
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                  account: account.account,
                  code: response.code,
                  redirect_origin: window.location.origin,
                }),
              });

              if (!reconnectResponse.ok) {
                throw new Error(await parseErrorMessage(reconnectResponse));
              }

              const payload = (await reconnectResponse.json()) as GoogleWorkspaceReconnectResponse;
              setWorkspaceNotice(
                `Reconnected ${payload.account}${payload.email ? ` as ${payload.email}` : ''}.`
              );
              await refreshWorkspaceStatus();
            } catch (error) {
              setWorkspaceError(
                error instanceof Error ? error.message : 'Google Workspace reconnect failed.'
              );
            } finally {
              setReconnectingAccount(null);
            }
          },
          error_callback: (error) => {
            const type = error?.type || 'unknown';
            setWorkspaceError(
              type === 'popup_closed'
                ? 'Google authorization was cancelled.'
                : 'Unable to open the Google authorization window.'
            );
            setReconnectingAccount(null);
          },
        });

        codeClient.requestCode();
      } catch (error) {
        setWorkspaceError(
          error instanceof Error ? error.message : 'Unable to start Google authorization.'
        );
        setReconnectingAccount(null);
      }
    },
    [refreshWorkspaceStatus, workspaceStatus]
  );

  if (!isOpen) return null;

  return (
    <>
      <div
        className="settings-backdrop"
        onClick={onClose}
      />

      <div className="settings-panel">
        <div className="settings-handle" onClick={onClose}>
          <div className="handle-bar" />
        </div>

        <div className="settings-header">
          <h3>Settings</h3>
          <button
            onClick={onClose}
            className="settings-close"
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="settings-content">
          <div className="settings-section capability-guide">
            <div className="capability-guide-heading">
              <div>
                <label className="settings-label">What AIDA can do</label>
                <p className="settings-description">
                  A live, versioned guide to AIDA&apos;s conversation tools and their safety rules.
                </p>
              </div>
              <button className="settings-action-btn" onClick={() => void refreshCapabilities()} disabled={capabilityLoading}>
                {capabilityLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            {capabilityCatalog && (
              <div className="capability-summary">
                <span>{capabilityCatalog.capability_count} capabilities</span>
                <span>registry {capabilityCatalog.capability_registry_version}</span>
              </div>
            )}
            <input
              className="capability-search"
              type="search"
              value={capabilityQuery}
              onChange={(event) => setCapabilityQuery(event.target.value)}
              placeholder="Search memory, drafts, reminders, web..."
              aria-label="Search AIDA capabilities"
            />
            {capabilityError && <div className="settings-inline-status error" role="alert">{capabilityError}</div>}
            {!capabilityLoading && capabilityCatalog && Object.keys(groupedCapabilities).length === 0 && (
              <div className="settings-empty">No capabilities match that search.</div>
            )}
            <div className="capability-groups">
              {Object.entries(groupedCapabilities).map(([category, capabilities]) => (
                <details className="capability-group" key={category} open={category === 'Memory & planning'}>
                  <summary>{category}<span>{capabilities.length}</span></summary>
                  <div className="capability-list">
                    {capabilities.map((capability) => (
                      <article className={`capability-card ${capability.available ? '' : 'unavailable'}`} key={capability.name}>
                        <div className="capability-card-heading">
                          <code>{capability.name}</code>
                          <span className={`capability-state ${capability.available ? 'ready' : 'attention'}`}>
                            {capability.available ? capability.access : 'unavailable'}
                          </span>
                        </div>
                        <p>{capability.description}</p>
                        {capability.confirmation !== 'none' && <small>Requires a separate confirmation turn.</small>}
                        {!capability.available && <small>{capability.availability_note}</small>}
                        {capability.limitations.map((limitation) => <small key={limitation}>{limitation}</small>)}
                      </article>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </div>

          <div className="settings-section">
            <label className="settings-label">Voice model</label>
            <p className="settings-description">All models use the same AIDA memory and production tools. Changes apply to the next voice session.</p>
            <div className="voice-grid" role="group" aria-label="Voice model">
              <button
                onClick={() => onVoiceRuntimeChange('gemini_live')}
                disabled={voiceSessionActive}
                aria-pressed={selectedVoiceRuntime === 'gemini_live'}
                className={`voice-option ${selectedVoiceRuntime === 'gemini_live' ? 'selected' : ''}`}
              >
                <span className="voice-name">Gemini Live</span>
                <span className="voice-description">Gemini 3.8 Live voice runtime</span>
              </button>
              <button
                onClick={() => onVoiceRuntimeChange('openai_realtime')}
                disabled={voiceSessionActive || !availableVoiceRuntimes.includes('openai_realtime')}
                aria-pressed={selectedVoiceRuntime === 'openai_realtime'}
                className={`voice-option ${selectedVoiceRuntime === 'openai_realtime' ? 'selected' : ''}`}
              >
                <span className="voice-name">GPT Realtime 2.1</span>
                <span className="voice-description">{availableVoiceRuntimes.includes('openai_realtime') ? 'GPT Realtime 2.1 with live web search' : 'Temporarily unavailable'}</span>
              </button>
              <button
                onClick={() => onVoiceRuntimeChange('openai_realtime_mini')}
                disabled={voiceSessionActive || !availableVoiceRuntimes.includes('openai_realtime_mini')}
                aria-pressed={selectedVoiceRuntime === 'openai_realtime_mini'}
                className={`voice-option ${selectedVoiceRuntime === 'openai_realtime_mini' ? 'selected' : ''}`}
              >
                <span className="voice-name">GPT Realtime 2.1 Mini</span>
                <span className="voice-description">{availableVoiceRuntimes.includes('openai_realtime_mini') ? 'Faster, lower-cost OpenAI realtime voice' : 'Temporarily unavailable'}</span>
              </button>
            </div>
          </div>

          <div className="settings-section">
            <label className="settings-label">Voice</label>
            <p className="settings-description">Choose AIDA&apos;s {selectedVoiceRuntime === 'gemini_live' ? 'Gemini Live' : 'OpenAI Realtime'} voice. Changes apply to the next voice session.</p>

            {selectedVoiceRuntime === 'gemini_live' && <div className="voice-grid" role="group" aria-label="Gemini Live voice">
              {GEMINI_VOICES.map((voice) => (
                <button
                  key={voice.id}
                  onClick={() => onVoiceChange(voice.id)}
                  aria-pressed={selectedVoice === voice.id}
                  className={`voice-option ${selectedVoice === voice.id ? 'selected' : ''}`}
                >
                  <span className="voice-name">{voice.name}</span>
                  <span className="voice-description">{voice.description}</span>
                  <span className="voice-gender">{voice.gender}</span>
                </button>
              ))}
            </div>}
            {isOpenAIRealtimeRuntime(selectedVoiceRuntime) && <div className="voice-grid" role="group" aria-label="OpenAI Realtime voice">
              {OPENAI_REALTIME_VOICES.filter((voice) => availableOpenAIRealtimeVoices.includes(voice.id)).map((voice) => (
                <button
                  key={voice.id}
                  onClick={() => onOpenAIRealtimeVoiceChange(voice.id)}
                  aria-pressed={selectedOpenAIRealtimeVoice === voice.id}
                  className={`voice-option ${selectedOpenAIRealtimeVoice === voice.id ? 'selected' : ''}`}
                >
                  <span className="voice-name">{voice.name}</span>
                  <span className="voice-description">OpenAI Realtime voice</span>
                </button>
              ))}
            </div>}
          </div>

          <div className="settings-section">
            <label className="settings-label">Visualizer</label>
            <p className="settings-description">Simple mode uses less battery on mobile</p>

            <div className="visualizer-toggle">
              <button
                onClick={() => onVisualizerModeChange('full')}
                className={`visualizer-option ${visualizerMode === 'full' ? 'selected' : ''}`}
              >
                <span className="option-name">Full</span>
                <span className="option-description">Animated waveform</span>
              </button>
              <button
                onClick={() => onVisualizerModeChange('simple')}
                className={`visualizer-option ${visualizerMode === 'simple' ? 'selected' : ''}`}
              >
                <span className="option-name">Simple</span>
                <span className="option-description">Better for mobile</span>
              </button>
            </div>
          </div>

          <div className="settings-section">
            <label className="settings-label">Debug Transcript</label>
            <p className="settings-description">Optional local event log for troubleshooting</p>

            <div className="visualizer-toggle">
              <button
                onClick={() => onShowDebugTranscriptChange(true)}
                className={`visualizer-option ${showDebugTranscript ? 'selected' : ''}`}
              >
                <span className="option-name">On</span>
                <span className="option-description">Show live event feed</span>
              </button>
              <button
                onClick={() => onShowDebugTranscriptChange(false)}
                className={`visualizer-option ${!showDebugTranscript ? 'selected' : ''}`}
              >
                <span className="option-name">Off</span>
                <span className="option-description">Hidden by default</span>
              </button>
            </div>
          </div>

          <div className="settings-section">
            <label className="settings-label">Google Workspace</label>
            <p className="settings-description">Reconnect Gmail and Calendar access for AIDA directly from this screen.</p>

            <div className="workspace-settings-panel">
              <div className="workspace-settings-header">
                <button
                  className="settings-action-btn"
                  onClick={() => void refreshWorkspaceStatus()}
                  disabled={workspaceLoading}
                >
                  {workspaceLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              {workspaceNotice && <div className="settings-inline-status success">{workspaceNotice}</div>}
              {workspaceError && <div className="settings-inline-status error">{workspaceError}</div>}

              {!workspaceLoading && !workspaceStatus?.accounts?.length && !workspaceError && (
                <div className="settings-empty">No Google Workspace accounts are configured.</div>
              )}

              <div className="workspace-account-list">
                {(workspaceStatus?.accounts || []).map((account) => {
                  const reconnecting = reconnectingAccount === account.account;
                  return (
                    <div className="workspace-account" key={account.account}>
                      <div className="workspace-account-row">
                        <div>
                          <div className="workspace-account-title">
                            {account.account}
                            {account.is_default && <span className="workspace-pill">default</span>}
                            <span className={`workspace-pill ${account.connected ? 'connected' : 'attention'}`}>
                              {account.connected ? 'connected' : 'needs reconnect'}
                            </span>
                          </div>
                          <div className="workspace-account-email">
                            {account.user || account.email_hint || 'No email available'}
                          </div>
                        </div>

                        <button
                          className="settings-action-btn"
                          onClick={() => void handleReconnect(account)}
                          disabled={reconnecting || !workspaceStatus?.oauth_reconnect_available}
                        >
                          {reconnecting ? 'Authorizing...' : 'Reconnect'}
                        </button>
                      </div>

                      <div className="workspace-capabilities">
                        <span className={`workspace-capability ${account.gmail_read_ready ? 'ready' : ''}`}>Gmail read</span>
                        <span className={`workspace-capability ${account.gmail_modify_ready ? 'ready' : ''}`}>Gmail modify</span>
                        <span className={`workspace-capability ${account.gmail_send_ready ? 'ready' : ''}`}>Gmail send</span>
                        <span className={`workspace-capability ${account.calendar_read_ready ? 'ready' : ''}`}>Calendar read</span>
                        <span className={`workspace-capability ${account.calendar_write_ready ? 'ready' : ''}`}>Calendar write</span>
                      </div>

                      {account.error && <div className="workspace-account-error">{account.error}</div>}
                    </div>
                  );
                })}
              </div>

              {!workspaceStatus?.oauth_reconnect_available && !workspaceError && (
                <div className="settings-inline-status error">
                  Google reconnect is not configured on the server.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPanel;
